import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { substituteIds, useEntityLabels } from "../../lib/labels";
export function AssistantPanel({ context, onClose }) {
    const qc = useQueryClient();
    const [sessionId, setSessionId] = useState(null);
    const [draft, setDraft] = useState("");
    const [err, setErr] = useState(null);
    const [pendingDocs, setPendingDocs] = useState([]);
    const fileInputRef = useRef(null);
    const scrollRef = useRef(null);
    // Lazy create a session on first send.
    const createSession = useMutation({
        mutationFn: () => api.post("/assistant/sessions", {
            title: null,
            context: context ?? {},
        }),
    });
    const messages = useQuery({
        queryKey: ["assistant-messages", sessionId],
        queryFn: () => api.get(`/assistant/sessions/${sessionId}/messages`),
        enabled: !!sessionId,
        refetchOnWindowFocus: false,
    });
    const send = useMutation({
        mutationFn: async (message) => {
            let sid = sessionId;
            if (!sid) {
                const s = await createSession.mutateAsync();
                sid = s.id;
                setSessionId(s.id);
            }
            return api.post(`/assistant/sessions/${sid}/messages`, {
                message, context: context ?? {},
                attached_document_ids: pendingDocs.map((d) => d.id),
            });
        },
        onSuccess: () => {
            setDraft("");
            setErr(null);
            setPendingDocs([]);
            qc.invalidateQueries({ queryKey: ["assistant-messages", sessionId] });
        },
        onError: (e) => {
            setErr(e instanceof ApiError ? e.message : "send failed");
        },
    });
    const upload = useMutation({
        mutationFn: async (file) => {
            let sid = sessionId;
            if (!sid) {
                const s = await createSession.mutateAsync();
                sid = s.id;
                setSessionId(s.id);
            }
            return api.upload(`/assistant/sessions/${sid}/uploads`, file);
        },
        onSuccess: (doc) => {
            setPendingDocs((prev) => [...prev, doc]);
            setErr(null);
            if (fileInputRef.current)
                fileInputRef.current.value = "";
        },
        onError: (e) => {
            setErr(e instanceof ApiError ? e.message : "upload failed");
        },
    });
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages.data, send.isPending]);
    function onSubmit(e) {
        e.preventDefault();
        const v = draft.trim();
        if (!v || send.isPending)
            return;
        send.mutate(v);
    }
    return (_jsxs("aside", { className: "fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col border-l border-ink-700 bg-ink-900 shadow-2xl", children: [_jsxs("header", { className: "flex items-center justify-between border-b border-ink-700 px-4 py-2", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-sm font-semibold text-ink-100", children: "AutoVal Assistant" }), _jsx("p", { className: "text-[11px] text-ink-500", children: scopeLine(context) || "Ask anything about your work" })] }), _jsx("button", { className: "btn-ghost text-xs", onClick: onClose, "aria-label": "Close", children: "\u00D7" })] }), _jsxs("div", { ref: scrollRef, className: "flex-1 overflow-y-auto px-4 py-3 space-y-3", children: [!sessionId && (_jsx("div", { className: "rounded-md border border-ink-700 bg-ink-800/40 p-3 text-xs text-ink-400", children: "Tell the assistant what you'd like to do. It can read across your data and propose changes for you to approve." })), messages.data?.map((m) => (_jsx(MessageBubble, { message: m, sessionId: sessionId }, m.id))), send.isPending && (_jsx("div", { className: "text-xs text-ink-500", children: "thinking\u2026" })), err && (_jsx("div", { className: "rounded-md border border-red-700/50 bg-red-900/20 p-2 text-xs text-red-200", children: err }))] }), _jsxs("form", { onSubmit: onSubmit, className: "border-t border-ink-700 bg-ink-800/40 px-3 py-2", children: [pendingDocs.length > 0 && (_jsx("ul", { className: "mb-2 flex flex-wrap gap-1.5 text-[11px]", children: pendingDocs.map((d) => (_jsxs("li", { className: "inline-flex items-center gap-1 rounded border border-ink-700 bg-ink-900/60 px-2 py-0.5 text-ink-200", children: [_jsx("span", { className: "font-mono", children: d.filename }), _jsx("button", { type: "button", className: "text-red-400 hover:text-red-300", onClick: () => setPendingDocs((prev) => prev.filter((x) => x.id !== d.id)), "aria-label": "remove attachment", children: "\u00D7" })] }, d.id))) })), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ".pdf,.png,.jpg,.jpeg,.txt,.csv,.md,.xlsx", className: "hidden", onChange: (e) => {
                                    const f = e.target.files?.[0];
                                    if (f)
                                        upload.mutate(f);
                                } }), _jsx("button", { type: "button", className: "btn-ghost flex-shrink-0 text-xs", disabled: upload.isPending, onClick: () => fileInputRef.current?.click(), title: "Attach a file (term sheet, annual report, etc.)", children: upload.isPending ? "Uploading…" : "📎" }), _jsx("textarea", { className: "input flex-1 resize-none text-xs", rows: 2, value: draft, onChange: (e) => setDraft(e.target.value), placeholder: "e.g. add MSFT to this portfolio and run a 31/12/2025 valuation", onKeyDown: (e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        onSubmit(e);
                                    }
                                } }), _jsx("button", { className: "btn-primary text-xs", disabled: send.isPending || (!draft.trim() && pendingDocs.length === 0), children: "Send" })] })] })] }));
}
function MessageBubble({ message, sessionId, }) {
    if (message.role === "user") {
        return (_jsx("div", { className: "flex justify-end", children: _jsx("div", { className: "max-w-[85%] rounded-lg bg-accent-500/15 px-3 py-2 text-xs text-ink-100 whitespace-pre-wrap", children: String(message.content?.text ?? "") }) }));
    }
    if (message.role === "plan") {
        const c = message.content;
        return _jsx(PlanCard, { planId: c.plan_id, summary: c.summary, steps: c.steps, sessionId: sessionId });
    }
    if (message.role === "plan_result") {
        const c = message.content;
        return _jsx(PlanResultCard, { status: c.status, execution: c.execution ?? [] });
    }
    // assistant text
    const text = String(message.content?.text ?? "");
    if (!text)
        return null;
    return (_jsx("div", { className: "flex justify-start", children: _jsx("div", { className: "max-w-[85%] rounded-lg bg-ink-800 px-3 py-2 text-xs text-ink-200 whitespace-pre-wrap", children: text }) }));
}
function PlanResultCard({ status, execution, }) {
    const okCount = execution.filter((s) => s.ok).length;
    const tone = status === "executed"
        ? "border-emerald-700/40 bg-emerald-900/10 text-emerald-100"
        : "border-amber-700/40 bg-amber-900/10 text-amber-100";
    return (_jsxs("div", { className: `rounded-md border p-3 text-xs ${tone}`, children: [_jsxs("div", { className: "mb-1 font-medium", children: ["Plan ", status, " \u00B7 ", okCount, "/", execution.length, " step", execution.length === 1 ? "" : "s", " OK"] }), _jsx("ol", { className: "list-decimal pl-4 space-y-1", children: execution.map((s, i) => (_jsxs("li", { children: [_jsx("span", { className: "font-mono", children: s.tool }), " ", s.ok ? (_jsx("span", { className: "text-emerald-300", children: "\u2713" })) : (_jsx("span", { className: "text-red-300", children: "\u2717" })), s.error && (_jsx("div", { className: "text-[11px] text-red-300", children: s.error })), s.ok && s.result && (_jsx("pre", { className: "mt-0.5 max-h-16 overflow-auto rounded bg-ink-900/40 p-1 font-mono text-[10px] text-ink-200", children: JSON.stringify(s.result, null, 2) }))] }, i))) })] }));
}
function PlanCard({ planId, summary, steps, sessionId, }) {
    const qc = useQueryClient();
    const [error, setError] = useState(null);
    const [started, setStarted] = useState(false);
    // Poll the plan once execution has been kicked off so we can show
    // step-by-step progress instead of a single opaque spinner.
    const planQ = useQuery({
        queryKey: ["assistant-plan", planId],
        queryFn: () => api.get(`/assistant/plans/${planId}`),
        enabled: started,
        refetchInterval: (q) => {
            const p = q.state.data;
            // Poll while running; stop when the worker has settled.
            return p && p.status === "running" ? 1500 : false;
        },
    });
    const plan = planQ.data;
    const approve = useMutation({
        mutationFn: () => api.post(`/assistant/plans/${planId}/approve`),
        onSuccess: (p) => {
            // Seed the polling cache with the running plan.
            qc.setQueryData(["assistant-plan", planId], p);
            setStarted(true);
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "approve failed"),
    });
    const reject = useMutation({
        mutationFn: () => api.post(`/assistant/plans/${planId}/reject`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-messages", sessionId] }),
    });
    // When the plan transitions out of `running`, refresh the message
    // list so the plan_result chat message lands in the thread, AND
    // invalidate every other query so any page the user is currently
    // looking at (instrument list, approach editor, valuation page, …)
    // re-fetches and shows whatever the plan just created/changed.
    const isDone = plan && plan.status !== "running" && plan.status !== "proposed";
    useEffect(() => {
        if (isDone) {
            qc.invalidateQueries({ queryKey: ["assistant-messages", sessionId] });
            // Broad refresh — assistant plans touch enough disparate
            // entities that targeted invalidation would miss something.
            qc.invalidateQueries();
        }
    }, [isDone, qc, sessionId]);
    const isRunning = plan?.status === "running" || (started && !plan);
    const completed = plan?.execution?.length ?? 0;
    const total = steps.length;
    const currentStepIdx = isRunning ? completed : -1;
    // Resolve UUIDs across the whole plan + execution result so the
    // pre-blocks below render `Atlantica Bank · instrument` instead of
    // `feb8758b-…`.
    const labels = useEntityLabels({ steps, plan: plan?.execution });
    return (_jsxs("div", { className: "rounded-md border border-amber-700/40 bg-amber-900/10 p-3 text-xs text-amber-100", children: [_jsxs("div", { className: "mb-2 flex items-center justify-between", children: [_jsx("div", { className: "font-medium text-amber-200", children: plan?.status === "running" ? "Plan running…" :
                            plan?.status === "executed" ? "Plan executed" :
                                plan?.status === "partial" ? "Plan partial — stopped on error" :
                                    plan?.status === "failed" ? "Plan failed" :
                                        plan?.status === "rejected" ? "Plan rejected" :
                                            "Plan proposed" }), isRunning && (_jsxs("div", { className: "text-[11px] text-amber-300/80", children: [completed, "/", total, " step", total === 1 ? "" : "s", " done"] }))] }), _jsx("div", { className: "mb-2 whitespace-pre-wrap", children: summary }), _jsx("ol", { className: "list-decimal pl-4 space-y-1", children: steps.map((s, i) => {
                    const exec = plan?.execution?.find((e) => e.step_index === i);
                    const isCurrent = i === currentStepIdx;
                    return (_jsxs("li", { children: [_jsx("span", { className: "font-mono text-amber-200", children: s.tool }), exec && exec.ok && _jsx("span", { className: "ml-1 text-emerald-300", children: "\u2713" }), exec && !exec.ok && _jsx("span", { className: "ml-1 text-red-300", children: "\u2717" }), isCurrent && (_jsxs("span", { className: "ml-1 inline-flex items-center gap-1 text-[11px] text-sky-300", children: [_jsx("span", { className: "inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-sky-400" }), "running\u2026"] })), s.severity === "warning" && (_jsx("span", { className: "ml-1 rounded bg-red-900/40 px-1 text-[10px] text-red-200", children: "review" })), s.why && _jsx("div", { className: "text-[11px] text-amber-300/80", children: s.why }), _jsx("pre", { className: "mt-0.5 max-h-32 overflow-auto rounded bg-ink-900/40 p-1 font-mono text-[10px] text-ink-200", children: JSON.stringify(substituteIds(s.args, labels), null, 2) }), s._invalid && (_jsxs("div", { className: "text-[11px] text-red-300", children: ["\u26A0 ", s._invalid] })), exec && !exec.ok && exec.error && (_jsx("div", { className: "mt-0.5 rounded border border-red-700/40 bg-red-900/20 p-1 text-[11px] text-red-200", children: exec.error })), exec && exec.ok && exec.result && (_jsx("pre", { className: "mt-0.5 max-h-32 overflow-auto rounded border border-emerald-700/30 bg-emerald-900/10 p-1 font-mono text-[10px] text-emerald-200", children: JSON.stringify(substituteIds(exec.result, labels), null, 2) }))] }, i));
                }) }), !started && !plan && (_jsxs("div", { className: "mt-2 flex gap-2", children: [_jsx("button", { className: "btn-primary text-[11px]", disabled: approve.isPending, onClick: () => approve.mutate(), children: approve.isPending ? "Starting…" : "Approve & run" }), _jsx("button", { className: "btn-ghost text-[11px]", disabled: approve.isPending || reject.isPending, onClick: () => reject.mutate(), children: "Reject" })] })), isDone && plan && plan.execution && (_jsxs("div", { className: "mt-2 text-[11px]", children: ["Plan ", _jsx("span", { className: "font-mono", children: plan.status }), " ", "\u2014", " ", plan.execution.filter((e) => e.ok).length, "/", plan.execution.length, " step(s) OK"] })), error && _jsx("div", { className: "mt-2 text-[11px] text-red-300", children: error })] }));
}
function scopeLine(ctx) {
    if (!ctx)
        return "";
    const keys = ["client_id", "portfolio_id", "instrument_id",
        "approach_id", "approach_version_id",
        "valuation_id", "valuation_version_id"];
    const set = keys.filter((k) => !!ctx[k]);
    if (set.length === 0)
        return "";
    return "scope: " + set.map((k) => k.replace("_id", "")).join(" → ");
}
